/**
 * Sprechen statt tippen - fuer den Assistenten auf dem Handy.
 *
 * Zwei Wege, der Browser entscheidet:
 *
 *   1. Spracherkennung im Browser (SpeechRecognition, in Safari als
 *      webkitSpeechRecognition). Schnell, kostenlos, liefert schon waehrend
 *      des Sprechens Zwischenstaende. Safari beendet von selbst, wenn man
 *      aufhoert zu reden.
 *   2. Aufnahme mit dem Mikrofon (MediaRecorder) und Umschrift auf dem
 *      Server (/assistent/transkript, OpenAI). Der Rueckfall fuer alles,
 *      was Weg 1 nicht kann - etwa die als App installierte Fassung auf dem
 *      iPhone, in der Safari die Erkennung verweigert.
 *
 * Beide Wege enden gleich: onFertig(text). Der Aufrufer schickt den Text
 * dann als Frage ab - "hands-free", ohne Tippen.
 *
 * Vorlesen (speechSynthesis) ist der Rueckweg: Wer per Sprache fragt, will
 * die Antwort meist hoeren, nicht lesen.
 */
import api from "@/lib/api";

const Erkennung = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export function erkennungVerfuegbar() {
  return !!Erkennung;
}

export function aufnahmeVerfuegbar() {
  return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

export function spracheingabeVerfuegbar() {
  return erkennungVerfuegbar() || aufnahmeVerfuegbar();
}

export function vorlesenVerfuegbar() {
  return "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

const AUFNAHME_MAX_MS = 30000;

/** Welches Aufnahmeformat der Browser kann - Safari mp4, Chrome webm. */
function aufnahmeFormat() {
  for (const f of ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]) {
    try { if (window.MediaRecorder.isTypeSupported(f)) return f; } catch { /* weiter */ }
  }
  return "";
}

/**
 * Startet die Spracheingabe.
 *
 *   onZwischen(text)  waehrend des Sprechens (nur Weg 1)
 *   onFertig(text)    der fertige Text - oder "" wenn nichts verstanden wurde
 *   onFehler(text)    ein Grund in Worten, wenn es nicht geht
 *   onZustand(z)      "hoert" | "verarbeitet" | "aus"
 *
 * Gibt { stop } zurueck. stop() beendet die Aufnahme und loest onFertig aus.
 */
export function spracheingabeStarten({ onZwischen, onFertig, onFehler, onZustand }) {
  const melde = (z) => { try { onZustand?.(z); } catch { /* egal */ } };

  // ---------------------------------------------- Weg 2: Aufnahme + Server
  const aufnehmen = async () => {
    if (!aufnahmeVerfuegbar()) {
      melde("aus");
      onFehler?.("Dieser Browser kann kein Mikrofon nutzen.");
      return { stop: () => {} };
    }
    let strom;
    try {
      strom = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      melde("aus");
      onFehler?.("Kein Zugriff auf das Mikrofon. In den Einstellungen für diese Seite erlauben.");
      return { stop: () => {} };
    }
    const format = aufnahmeFormat();
    const rekorder = format ? new MediaRecorder(strom, { mimeType: format }) : new MediaRecorder(strom);
    const stuecke = [];
    let beendet = false;
    rekorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) stuecke.push(e.data); };
    rekorder.onstop = async () => {
      strom.getTracks().forEach((t) => t.stop());
      melde("verarbeitet");
      try {
        const blob = new Blob(stuecke, { type: rekorder.mimeType || format || "audio/webm" });
        if (blob.size < 1500) { melde("aus"); onFertig?.(""); return; }
        const endung = (rekorder.mimeType || format || "").includes("mp4") ? "m4a"
          : (rekorder.mimeType || format || "").includes("ogg") ? "ogg" : "webm";
        const form = new FormData();
        form.append("datei", blob, `aufnahme.${endung}`);
        const r = await api.post("/assistent/transkript", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        melde("aus");
        onFertig?.((r.data?.text || "").trim());
      } catch (e) {
        melde("aus");
        onFehler?.(e.response?.data?.detail || "Die Aufnahme konnte nicht in Text umgewandelt werden.");
      }
    };
    rekorder.start(250);
    melde("hoert");
    const wecker = setTimeout(() => { if (!beendet) stop(); }, AUFNAHME_MAX_MS);
    const stop = () => {
      if (beendet) return;
      beendet = true;
      clearTimeout(wecker);
      try { rekorder.state !== "inactive" && rekorder.stop(); } catch { /* egal */ }
    };
    return { stop };
  };

  // ------------------------------------------ Weg 1: Erkennung im Browser
  if (!Erkennung) return aufnehmen();

  const erk = new Erkennung();
  erk.lang = "de-DE";
  erk.interimResults = true;
  erk.continuous = false;
  erk.maxAlternatives = 1;
  let fertigText = "";
  let abgegeben = false;
  let rueckfall = null;

  const abgeben = (text) => {
    if (abgegeben) return;
    abgegeben = true;
    melde("aus");
    onFertig?.((text || "").trim());
  };

  erk.onresult = (e) => {
    let zwischen = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) fertigText += r[0].transcript;
      else zwischen += r[0].transcript;
    }
    onZwischen?.((fertigText + zwischen).trim());
  };
  erk.onerror = async (e) => {
    // Safari als installierte App, fehlende Freigabe, kein Netz fuer den
    // Erkennungsdienst: dann die Aufnahme mit Umschrift auf dem Server.
    if (["not-allowed", "service-not-allowed", "audio-capture", "network", "language-not-supported"].includes(e.error)
        && aufnahmeVerfuegbar() && !abgegeben) {
      abgegeben = true;              // die Erkennung ist damit erledigt
      rueckfall = await aufnehmen();
      return;
    }
    if (e.error === "no-speech" || e.error === "aborted") { abgeben(fertigText); return; }
    if (!abgegeben) { abgegeben = true; melde("aus"); onFehler?.("Spracheingabe nicht möglich (" + e.error + ")."); }
  };
  erk.onend = () => { if (!rueckfall) abgeben(fertigText); };
  try {
    erk.start();
    melde("hoert");
  } catch {
    return aufnehmen();
  }
  return {
    stop: () => {
      if (rueckfall) { rueckfall.stop(); return; }
      try { erk.stop(); } catch { abgeben(fertigText); }
    },
  };
}

// -------------------------------------------------------------- Vorlesen

let stimme = null;

function stimmeWaehlen() {
  if (stimme || !vorlesenVerfuegbar()) return stimme;
  const alle = window.speechSynthesis.getVoices() || [];
  const deutsch = alle.filter((v) => (v.lang || "").toLowerCase().startsWith("de"));
  // Apples "Anna" (de-DE) klingt am natuerlichsten; sonst die erste deutsche.
  stimme = deutsch.find((v) => /anna|petra|helena/i.test(v.name)) || deutsch[0] || null;
  return stimme;
}

/** Liest einen Text vor; ein laufender wird abgebrochen. */
export function vorlesen(text) {
  if (!vorlesenVerfuegbar() || !text) return;
  try {
    window.speechSynthesis.cancel();
    const sauber = String(text).replace(/[*_#`>]/g, "").replace(/\s+/g, " ").trim();
    const u = new SpeechSynthesisUtterance(sauber);
    u.lang = "de-DE";
    u.rate = 1.05;
    const v = stimmeWaehlen();
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch { /* dann eben still */ }
}

export function vorlesenStop() {
  try { window.speechSynthesis?.cancel(); } catch { /* egal */ }
}

if (vorlesenVerfuegbar()) {
  // Stimmen kommen in Safari erst nach dem ersten Ereignis.
  try { window.speechSynthesis.onvoiceschanged = () => { stimme = null; stimmeWaehlen(); }; } catch { /* egal */ }
}
